(function() {
  'use strict';
  
  class PromoCarousel {
    constructor() {
      this.triggerContainer = document.getElementById('promo-carousel');
      this.trigger = document.querySelector('.promo-carousel__trigger');
      this.drawer = document.getElementById('promo-carousel-drawer');
      
      if (!this.trigger || !this.drawer) return;
      
      this.overlay = this.drawer.querySelector('.promo-carousel-drawer__overlay');
      this.slider = this.drawer.querySelector('.promo-carousel-drawer__slider');
      this.prevButton = this.drawer.querySelector('.promo-carousel__nav--prev');
      this.nextButton = this.drawer.querySelector('.promo-carousel__nav--next');
      this.slides = this.drawer.querySelectorAll('.promo-carousel__slide');
      this.resizeObserver = null;
      
      this.init();
    }
    
    init() {
      // Trigger toggles drawer (open when closed, close when open)
      this.trigger?.addEventListener('click', () => this.toggleDrawer());
      this.overlay?.addEventListener('click', () => this.closeDrawer());

      // Close drawer if chat button is clicked while drawer is open
      const chatBtn = this.triggerContainer?.querySelector('.promo-pill__chat');
      chatBtn?.addEventListener('click', () => {
        if (this.drawer?.getAttribute('aria-hidden') === 'false') {
          this.trigger?.click();
        }
      });
      
      // Navigation arrows
      this.prevButton?.addEventListener('click', () => this.scrollPrev());
      this.nextButton?.addEventListener('click', () => this.scrollNext());
      
      // Update arrow states on scroll
      this.slider?.addEventListener('scroll', () => this.updateArrowStates());
      
      // Information panel: open on info icon click, close on X click
      this.drawer?.addEventListener('click', (e) => this.handleDrawerClick(e));
      
      // Keyboard navigation
      document.addEventListener('keydown', (e) => this.handleKeyboard(e));
      
      // Equalize heights on resize
      this.setupHeightEqualization();
    }
    
    /**
     * @param {MouseEvent} e
     */
    handleDrawerClick(e) {
      const target = /** @type {Element} */ (e.target);
      const trigger = target.closest('.promo-carousel__slide-info-trigger');
      const closeBtn = target.closest('.promo-carousel__slide-info-close');
      if (trigger) {
        e.preventDefault();
        const panelId = trigger.getAttribute('aria-controls');
        const panel = panelId ? this.drawer?.querySelector(`#${panelId}`) : null;
        if (panel) {
          const isOpen = panel.getAttribute('aria-hidden') === 'false';
          panel.setAttribute('aria-hidden', isOpen ? 'true' : 'false');
          trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        }
      } else if (closeBtn) {
        e.preventDefault();
        const panel = closeBtn.closest('.promo-carousel__slide-info-panel');
        if (panel) {
          panel.setAttribute('aria-hidden', 'true');
          const triggerBtn = this.drawer?.querySelector(`[aria-controls="${panel.id}"]`);
          if (triggerBtn) triggerBtn.setAttribute('aria-expanded', 'false');
        }
      }
    }
    
    toggleDrawer() {
      if (this.drawer?.getAttribute('aria-hidden') === 'false') {
        this.closeDrawer();
      } else {
        this.openDrawer();
      }
    }

    openDrawer() {
      this.drawer?.setAttribute('aria-hidden', 'false');
      this.triggerContainer?.setAttribute('data-drawer-open', '');
      document.body.style.overflow = 'hidden';
      this.trigger?.setAttribute('aria-label', 'Close promotions');
      // Update arrow states and heights when drawer opens
      setTimeout(() => {
        this.updateArrowStates();
        this.equalizeHeights();
      }, 100);
    }

    closeDrawer() {
      this.drawer?.setAttribute('aria-hidden', 'true');
      this.triggerContainer?.removeAttribute('data-drawer-open');
      document.body.style.overflow = '';
      this.trigger?.setAttribute('aria-label', 'View promotions');
    }
    
    /**
     * @param {KeyboardEvent} e
     */
    handleKeyboard(e) {
      // Only handle escape when drawer is open
      if (this.drawer?.getAttribute('aria-hidden') === 'false' && e.key === 'Escape') {
        this.closeDrawer();
      }
    }
    
    scrollPrev() {
      if (!this.slider) return;
      
      const firstSlide = /** @type {HTMLElement | undefined} */ (this.slides?.[0]);
      const slideWidth = firstSlide?.offsetWidth ?? 0;
      const gap = 20; // Match CSS gap
      this.slider.scrollBy({
        left: -(slideWidth + gap),
        behavior: 'smooth'
      });
    }
    
    scrollNext() {
      if (!this.slider) return;
      
      const firstSlide = /** @type {HTMLElement | undefined} */ (this.slides?.[0]);
      const slideWidth = firstSlide?.offsetWidth ?? 0;
      const gap = 20; // Match CSS gap
      this.slider.scrollBy({
        left: slideWidth + gap,
        behavior: 'smooth'
      });
    }
    
    updateArrowStates() {
      if (!this.slider || !this.prevButton || !this.nextButton) return;
      
      const scrollLeft = this.slider.scrollLeft;
      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
      
      // Disable prev button at start
      if (scrollLeft <= 1) {
        this.prevButton.setAttribute('disabled', '');
      } else {
        this.prevButton.removeAttribute('disabled');
      }
      
      // Disable next button at end
      if (scrollLeft >= maxScroll - 1) {
        this.nextButton.setAttribute('disabled', '');
      } else {
        this.nextButton.removeAttribute('disabled');
      }
    }
    
    setupHeightEqualization() {
      // Set up ResizeObserver to re-equalize heights on window resize
      this.resizeObserver = new ResizeObserver(() => {
        if (this.drawer?.getAttribute('aria-hidden') === 'false') {
          this.equalizeHeights();
        }
      });
      
      if (this.slider) {
        this.resizeObserver.observe(this.slider);
      }
    }
    
    equalizeHeights() {
      if (!this.drawer || !this.slides || this.slides.length === 0) return;
      
      const titles = this.drawer.querySelectorAll('.promo-carousel__slide-title');
      const descriptions = this.drawer.querySelectorAll('.promo-carousel__slide-description');
      
      // Reset title heights only (do not equalize descriptions – they would grow too tall and push the button off-screen)
      titles.forEach(title => { (/** @type {HTMLElement} */ (title)).style.height = 'auto'; });
      descriptions.forEach(desc => { (/** @type {HTMLElement} */ (desc)).style.height = 'auto'; });
      
      let maxTitleHeight = 0;
      titles.forEach(title => {
        maxTitleHeight = Math.max(maxTitleHeight, (/** @type {HTMLElement} */ (title)).offsetHeight);
      });
      
      if (maxTitleHeight > 0) {
        titles.forEach(title => {
          (/** @type {HTMLElement} */ (title)).style.height = `${maxTitleHeight}px`;

          
        });
      }
    }
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new PromoCarousel());
  } else {
    new PromoCarousel();
  }
})();